import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Check as CheckIcon,
  SwapHoriz as SwapIcon,
  People as PeopleIcon,
  ExitToApp as LeaveIcon,
  PersonAdd as JoinIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import { familyCodeService } from '../services/familyCodeService';
import inviteService from '../services/inviteService';
import logger from '../utils/logger';

/**
 * Dialog shown when a logged-in user accepts a family invitation.
 * Handles the full flow: leave current family → join new family → refresh context.
 *
 * This is for ALREADY LOGGED-IN users (as opposed to ExistingAccountDialog
 * which handles users discovered during signup).
 */
const InviteAcceptDialog = ({ open, onClose, invite, onSuccess }) => {
  const { currentUser, refreshUserData } = useAuth();
  const { profile, refreshProfile } = useProfile();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [familyInfo, setFamilyInfo] = useState(null);
  const [loadingFamily, setLoadingFamily] = useState(false);

  const currentFamilyCode = profile?.familyCode || currentUser?.familyCode;
  const isOwner = profile?.role === 'owner';

  // Load target family info when dialog opens
  useEffect(() => {
    const loadFamilyInfo = async () => {
      if (!open || !invite?.familyCode) return;

      setLoadingFamily(true);
      try {
        const family = await familyCodeService.getFamilyByCode(invite.familyCode);
        setFamilyInfo(family);
      } catch (err) {
        logger.error('Error loading family info:', err);
      } finally {
        setLoadingFamily(false);
      }
    };

    loadFamilyInfo();
  }, [open, invite?.familyCode]);

  const handleAcceptAndJoin = async () => {
    if (!currentUser?.uid || !invite) return;

    setLoading(true);
    setError('');

    try {
      // Step 1: Leave current family (if user is in one)
      if (currentFamilyCode) {
        await familyCodeService.leaveFamily(currentUser.uid);
      }

      // Step 2: Join the new family
      await familyCodeService.joinFamily(currentUser.uid, invite.familyCode, {
        relationship: invite.relationship || 'other',
        autoAssignGroups: invite.autoAssignGroups || ['IMMEDIATE_FAMILY'],
        invitedBy: invite.inviterId
      });

      // Step 3: Mark the old invite as accepted
      try {
        await inviteService.acceptInvite(invite.id);
      } catch (err) {
        // Non-critical — the join already happened
        logger.warn('Error updating invite status:', err);
      }

      // Step 4: Refresh auth + profile contexts
      if (refreshUserData) await refreshUserData();
      if (refreshProfile) await refreshProfile();

      // Done
      onSuccess?.(invite.familyCode);
    } catch (err) {
      logger.error('Error accepting invite:', err);

      if (err.message?.includes('owner cannot leave')) {
        setError('You are the owner of your current family. Transfer ownership before switching families.');
      } else {
        setError(err.message || 'Failed to join family. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    setError('');

    try {
      await inviteService.declineInvite(invite.id);
      onClose?.('declined');
    } catch (err) {
      logger.error('Error declining invite:', err);
      setError('Failed to decline invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose?.();
    }
  };

  const targetFamilyName = familyInfo?.name || invite?.familyCode;
  const inviterName = invite?.inviterName || invite?.inviterEmail || 'A family member';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <JoinIcon color="primary" />
          Family Invitation
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loadingFamily ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              <strong>{inviterName}</strong> has invited you to join{' '}
              <strong>{targetFamilyName}</strong>.
            </Typography>

            {invite?.relationship && (
              <Chip
                label={`Relationship: ${invite.relationship}`}
                size="small"
                sx={{ mt: 1, mb: 2 }}
              />
            )}

            <Divider sx={{ my: 2 }} />

            {/* Show what will happen */}
            {currentFamilyCode ? (
              <>
                {/* User is currently in a family */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    What will happen:
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <LeaveIcon fontSize="small" color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`You'll leave your current family (${currentFamilyCode})`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <JoinIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`You'll join ${targetFamilyName}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Your account and personal data are preserved"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Alert>

                {isOwner && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      You are the <strong>owner</strong> of your current family.
                      You must transfer ownership to another member before you can
                      leave and join a new family.
                    </Typography>
                  </Alert>
                )}

                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1
                  }}
                >
                  <Box textAlign="center">
                    <PeopleIcon color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {currentFamilyCode}
                    </Typography>
                  </Box>
                  <SwapIcon color="primary" />
                  <Box textAlign="center">
                    <PeopleIcon color="primary" />
                    <Typography variant="body2" color="primary">
                      {targetFamilyName}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                {/* User has no current family */}
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    What will happen:
                  </Typography>
                  <List dense disablePadding>
                    <ListItem sx={{ py: 0, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <JoinIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`You'll join ${targetFamilyName}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem sx={{ py: 0, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="You'll have access to the family's shared content"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>
                </Alert>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleDecline}
          disabled={loading}
          color="inherit"
        >
          Decline
        </Button>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Not Now
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAcceptAndJoin}
          disabled={loading || isOwner || loadingFamily}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
        >
          {loading
            ? 'Joining...'
            : currentFamilyCode
              ? 'Leave & Join'
              : 'Accept & Join'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteAcceptDialog;
